/*
 * Ghedee Philosophy — theme behaviour.
 * Everything lives inside one IIFE so nothing leaks to the global scope.
 * A single passive scroll listener fans out to registered callbacks via rAF.
 */
(function () {
  'use strict';

  // ─── Shared DOM refs (queried once) ──────────────────────────
  const els = {
    mobileNav: document.getElementById('mobileNav'),
    menuBtn:   document.getElementById('menuBtn'),
    nav:       document.querySelector('nav')
  };

  // ─── Single throttled scroll dispatcher ──────────────────────
  // Modules push a callback; all run once per animation frame.
  const scrollCallbacks = [];
  let scrollTicking = false;
  function runScrollCallbacks() {
    for (let i = 0; i < scrollCallbacks.length; i++) scrollCallbacks[i]();
    scrollTicking = false;
  }
  function onScroll() {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(runScrollCallbacks);
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  // ─── Translated UI strings ───────────────────────────────────
  // Emitted server-side (localized) by snippets/i18n-strings.liquid as the
  // #ghedee-i18n JSON block, which the layout renders on every page. That
  // snippet + Shopify's per-key `t` fallback are the single source of truth,
  // so there are no duplicate English copies here.
  const I18N = (function () {
    const el = document.getElementById('ghedee-i18n');
    try { return el ? JSON.parse(el.textContent) : {}; }
    catch (_) { return {}; }
  })();

  // ─── 18 Universal Laws data ──────────────────────────────────
  // Emitted server-side as a JSON <script> by the layout, then parsed here.
  function getLaws() {
    const el = document.getElementById('ghedee-laws-data');
    if (!el) return [];
    try {
      const data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data.sort(function (a, b) { return a.n - b.n; }) : [];
    } catch (_) {
      return [];
    }
  }

  // ─── Law state (module scope so the delegated close can clear it) ─
  let activeDetail = null;
  let activeLaw = null;

  function toggleLaw(law, card) {
    if (activeDetail) {
      activeDetail.remove();
      activeDetail = null;
      if (activeLaw) activeLaw.classList.remove('active');
      if (activeLaw === card) { activeLaw = null; return; }
    }
    activeLaw = card;
    card.classList.add('active');

    const detail = document.createElement('div');
    detail.className = 'law-detail visible';
    // law.statement / law.meaning are trusted merchant metafields that contain
    // intentional rich-text formatting (<em>, <strong>), so render as HTML.
    const lawTitle = law.title || ('Law ' + law.n + ': ' + law.name);
    detail.innerHTML =
      '<div class="law-detail-title">' + lawTitle + '</div>' +
      '<div class="law-detail-statement">' + law.statement + '</div>' +
      '<div class="law-detail-meaning">' + law.meaning + '</div>' +
      '<button class="law-close">' + I18N.close + ' ✕</button>';

    card.insertAdjacentElement('afterend', detail);
    activeDetail = detail;
    setTimeout(function () { detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); }, 50);
  }

  function initLawsGrid() {
    const grid = document.getElementById('lawsGrid');
    if (!grid) return;
    getLaws().forEach(function (law) {
      const card = document.createElement('div');
      card.className = 'law-card';
      card.innerHTML = '<div class="law-name">' + law.n + '. ' + law.name + '</div>';
      card.addEventListener('click', function () { toggleLaw(law, card); });
      grid.appendChild(card);
    });
  }

  // ─── Mobile nav ───────────────────────────────────────────────
  // setMobileNavState(open) is the single source of truth.
  function setMobileNavState(open) {
    if (!els.mobileNav) return;
    els.mobileNav.classList.toggle('open', open);
    if (els.menuBtn) {
      els.menuBtn.setAttribute('aria-expanded', String(open));
      els.menuBtn.classList.toggle('is-open', open); // CSS handles icon visibility
    }
  }
  function closeMobileNav() { setMobileNavState(false); }

  function initMobileNav() {
    if (els.menuBtn) {
      els.menuBtn.addEventListener('click', function () {
        setMobileNavState(!els.mobileNav.classList.contains('open'));
      });
    }
    // Close when a nav link is clicked.
    if (els.mobileNav) {
      els.mobileNav.addEventListener('click', function (e) {
        if (e.target.closest('a')) closeMobileNav();
      });
    }
  }

  // ─── Nav shadow on scroll ────────────────────────────────────
  function initNavShadow() {
    if (!els.nav) return;
    scrollCallbacks.push(function () {
      els.nav.classList.toggle('is-scrolled', window.scrollY > 10);
    });
  }

  // ─── Back to top ─────────────────────────────────────────────
  function initBackToTop() {
    const btn = document.getElementById('backToTop');
    if (!btn) return;
    scrollCallbacks.push(function () {
      btn.classList.toggle('visible', window.scrollY > 400);
    });
    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  // ─── Delegated document click ─────────────────────────────────
  function initDelegatedClick() {
    document.addEventListener('click', function (e) {
      // Law detail close button
      if (e.target.matches('.law-close')) {
        const detail = e.target.closest('.law-detail');
        if (detail) detail.remove();
        document.querySelectorAll('.law-card.active').forEach(function (c) {
          c.classList.remove('active');
        });
        activeDetail = null;
        activeLaw = null;
      }

      // Close mobile nav when clicking outside it
      if (
        els.mobileNav && els.mobileNav.classList.contains('open') &&
        !els.mobileNav.contains(e.target) &&
        els.menuBtn && !els.menuBtn.contains(e.target)
      ) {
        closeMobileNav();
      }
    });
  }

  // ─── Waitlist form ────────────────────────────────────────────
  function handleFormSuccess(btn, message) {
    btn.textContent = message;
    btn.classList.add('btn--success');
    btn.disabled = true;
  }
  function setWaitlistStatus(el, type, msg) {
    if (!el) return;
    el.textContent = msg;
    el.className = type ? 'waitlist-status waitlist-status--' + type : 'waitlist-status';
  }

  function initWaitlistForm() {
    const form = document.querySelector('.fallback-form');
    if (!form) return;

    form.addEventListener('submit', async function (e) {
      e.preventDefault();
      const btn = this.querySelector('button[type="submit"]');
      const emailInput = this.querySelector('input[type="email"]');
      const firstNameInput = this.querySelector('input[name="firstName"]');
      const statusEl = this.parentElement.querySelector('.waitlist-status');
      const email = emailInput.value.trim().toLowerCase();
      const firstName = firstNameInput ? firstNameInput.value.trim() : '';
      const apiKey = this.dataset.klaviyoApiKey;
      const listId = this.dataset.klaviyoList;
      const locale = this.dataset.locale;

      btn.disabled = true;
      btn.textContent = I18N.joining;
      setWaitlistStatus(statusEl, '', '');

      // If Klaviyo not configured in theme settings, skip the API call.
      if (!apiKey || !listId) {
        handleFormSuccess(btn, I18N.on_the_list);
        setWaitlistStatus(statusEl, 'success', I18N.welcome);
        return;
      }

      try {
        const profileAttrs = { email: email };
        if (firstName) profileAttrs.first_name = firstName;
        // Klaviyo's standard "Locale" profile attribute (BCP 47, e.g. en, es, zh-CN).
        if (locale) profileAttrs.locale = locale;

        const res = await fetch(
          'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(apiKey),
          {
            method: 'POST',
            headers: { 'content-type': 'application/json', 'revision': '2026-04-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'Waitlist Form',
                  profile: { data: { type: 'profile', attributes: profileAttrs } }
                },
                relationships: { list: { data: { type: 'list', id: listId } } }
              }
            })
          }
        );

        if (res.ok) {
          handleFormSuccess(btn, I18N.on_the_list);
          setWaitlistStatus(statusEl, 'success', I18N.welcome);
        } else {
          throw new Error(res.status);
        }
      } catch (_) {
        btn.disabled = false;
        btn.textContent = I18N.submit;
        setWaitlistStatus(statusEl, 'error', I18N.error);
      }
    });
  }

  // ─── Publication popup ────────────────────────────────────────
  // A corner card shown a beat after load, on a visitor's first visit only.
  // Opened non-modally (.show(), not .showModal()) so the page behind it stays
  // scrollable and clickable. The CTA closes it and then follows its own href
  // down to the #waitlist section.
  const POPUP_SEEN_KEY = 'ghedee_publication_popup_seen';
  const POPUP_DELAY_MS = 1200;

  function initWaitlistModal() {
    const modal = document.getElementById('waitlistModal');
    if (!modal) return;

    function close() {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    }

    modal.addEventListener('click', function (e) {
      // The CTA closes first, then its own navigation carries on uninterrupted.
      if (e.target.closest('[data-waitlist-close]') || e.target.closest('[data-waitlist-cta]')) {
        close();
      }
    });
    // Only a modal dialog closes itself on Esc, so wire it up by hand.
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.open) close();
    });

    let seen = null;
    try { seen = localStorage.getItem(POPUP_SEEN_KEY); } catch (_) { seen = '1'; }
    if (seen) return;

    setTimeout(function () {
      try { localStorage.setItem(POPUP_SEEN_KEY, '1'); } catch (_) { /* nothing to do */ }
      if (typeof modal.show === 'function') modal.show();
      else modal.setAttribute('open', '');
    }, POPUP_DELAY_MS);
  }

  // ─── Reading progress bar (article pages) ────────────────────
  function initReadingProgress() {
    const fill = document.getElementById('readingProgress');
    const jumpBtn = document.getElementById('jumpBtn');
    if (!fill) return;

    function updateProgress() {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      const pct = docH > 0 ? (window.scrollY / docH) * 100 : 0;
      fill.style.width = Math.min(pct, 100) + '%';
    }
    scrollCallbacks.push(updateProgress);
    updateProgress();

    if (jumpBtn) {
      const jumpList = document.getElementById('jumpList');
      jumpBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        const opening = !jumpBtn.classList.contains('open');
        jumpBtn.classList.toggle('open');
        // On open, scroll the current post into the middle of the capped list.
        if (opening && jumpList) {
          const current = jumpList.querySelector('[data-current]');
          if (current) {
            requestAnimationFrame(function () {
              const listRect = jumpList.getBoundingClientRect();
              const curRect = current.getBoundingClientRect();
              jumpList.scrollTop +=
                (curRect.top - listRect.top) -
                jumpList.clientHeight / 2 +
                current.clientHeight / 2;
            });
          }
        }
      });
      document.addEventListener('click', function (e) {
        if (!jumpBtn.contains(e.target)) jumpBtn.classList.remove('open');
      });
    }
  }

  // ─── Article share: copy link to clipboard ──────────────────
  function initShareButtons() {
    const copyBtn = document.querySelector('.article-share-copy');
    if (!copyBtn) return;

    let resetTimer;
    async function copyLink() {
      const url = copyBtn.dataset.shareUrl || location.href;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          // Fallback for non-secure contexts / older browsers.
          const ta = document.createElement('textarea');
          ta.value = url;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        copyBtn.classList.add('is-copied');
        clearTimeout(resetTimer);
        resetTimer = setTimeout(function () {
          copyBtn.classList.remove('is-copied');
        }, 2000);
      } catch (_) {
        // Clipboard blocked — leave the button unchanged.
      }
    }

    copyBtn.addEventListener('click', copyLink);
  }

  // ─── FAQ page (filters + accordion) ──────────────────────────
  function initFaq() {
    const filters = document.querySelectorAll('.faq-filter');
    const groups = document.querySelectorAll('.faq-group');
    const questions = document.querySelectorAll('.faq-question');
    if (!filters.length && !questions.length) return;

    filters.forEach(function (btn) {
      btn.addEventListener('click', function () {
        filters.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');

        const filter = btn.dataset.filter;
        groups.forEach(function (g) {
          g.style.display = (filter === 'all' || g.dataset.group === filter) ? '' : 'none';
        });
      });
    });

    questions.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const item = btn.closest('.faq-item');
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        document.querySelectorAll('.faq-item').forEach(function (i) {
          i.classList.remove('is-open');
          i.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // ─── Policy pages: open external links in a new tab ──────────
  function initPolicyLinks() {
    document.querySelectorAll('.policy-inner a').forEach(function (a) {
      if (a.hostname && a.hostname !== location.hostname) {
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noopener noreferrer');
      }
    });
  }

  // ─── Init ─────────────────────────────────────────────────────
  initLawsGrid();
  initMobileNav();
  initNavShadow();
  initBackToTop();
  initDelegatedClick();
  initWaitlistForm();
  initWaitlistModal();
  initReadingProgress();
  initShareButtons();
  initFaq();
  initPolicyLinks();
})();
